import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { PageHeader } from '@/shared/components/page-header';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Spinner } from '@/shared/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { maskCpfFromStored, maskPhoneFromStored } from '@/shared/lib/input-masks';
import { fetchInfluencers } from '../api/use-influencers';
import { InfluencerFormDialog } from './influencer-form-dialog';

export function InfluencerListPage() {
  const { idPassbook = '' } = useParams<{ idPassbook: string }>();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['influencers', idPassbook],
    queryFn: () => fetchInfluencers(idPassbook),
    enabled: Boolean(idPassbook),
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter(
      (u) =>
        (u.profile?.name ?? '').toLowerCase().includes(term) ||
        (u.profile?.uniqueId ?? '').includes(term),
    );
  }, [data, search]);

  return (
    <div>
      <PageHeader
        title="Parceiros"
        description="Participantes cadastrados internamente com o cartão de parceiro."
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-1 size-4" />
            Cadastrar parceiro
          </Button>
        }
      />

      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou CPF"
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          {data.length === 0
            ? 'Nenhum parceiro cadastrado nesta loja.'
            : 'Nenhum parceiro encontrado para essa busca.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>CPF</TableHead>
                <TableHead>Telefone</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.profile?.name ?? '—'}</TableCell>
                  <TableCell>{user.email || '—'}</TableCell>
                  <TableCell>{maskCpfFromStored(user.profile?.uniqueId)}</TableCell>
                  <TableCell>{maskPhoneFromStored(user.phone)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <InfluencerFormDialog
        idPassbook={idPassbook}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => void refetch()}
      />
    </div>
  );
}
